import { useEffect, useState } from "react"
import { DropdownInputDef, CampaignFeatureConfig, StepInputDef, DateTimeInputDef } from "../../interfaces/campaignConfig"
import apiClient from "../../axiosClient"
import { Box, Paper, TextField, Typography } from "@mui/material"
import DropDownValue from "../baseComponents/dropdownvalue"
import useIsMounted from "../../utils/useMountedRef"

export default function StepOneConfig({
    feature,
    inputsState,
    setInputsState,
}: {
    feature: CampaignFeatureConfig
    inputsState: Record<string, any>
    setInputsState: (s: Record<string, any>) => void
}) {
    // For dropdown inputs we fetch options per-input (or use provided items)
    const [optionsCache, setOptionsCache] = useState<Record<string, any[] | undefined>>({});
    const [loadingFetch, setLoadingFetch] = useState<Record<string, boolean | undefined>>({});
    const isMountedRef = useIsMounted();

    useEffect(() => {
        // prefetch all dropdowns
        const dropdowns = (feature.inputs ?? []).filter((i) => (i as any).type === 'DROPDOWN') as DropdownInputDef[]
        dropdowns.forEach((d) => fetchOptionsFor(d))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [feature.key])

    const fetchOptionsFor = async (d: DropdownInputDef) => {
        // if input defines static items, use them directly
        if (d.items && Array.isArray(d.items)) {
            if (!isMountedRef.current) return;
            setOptionsCache((s) => ({ ...s, [d.name]: d.items }));
            return;
        }

        const endpoint = d.fetchEndpoint;
        if (!endpoint) return;
        setLoadingFetch((s) => ({ ...s, [d.name]: true }));
        try {
            const res = await apiClient.get(endpoint);
            if (res.status === 200) {
                if (!isMountedRef.current) return;
                setOptionsCache((s) => ({ ...s, [d.name]: res.data }));
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            console.error('fetch dropdown options err', err);
        } finally {
            if (isMountedRef.current) {
                setLoadingFetch((s) => ({ ...s, [d.name]: false }));
            }
        }
    };

    const renderInput = (input: StepInputDef) => {
        if (input.type === 'TEXTFIELD') {
            return (
                <Box key={input.name} mb={2}>
                    <TextField
                        fullWidth
                        label={input.label}
                        placeholder={input.placeholder}
                        value={inputsState[input.name] ?? ''}
                        onChange={(e) => setInputsState({ ...inputsState, [input.name]: e.target.value })}
                    />
                </Box>
            )
        }

        if (input.type === 'DATETIME') {
            const dtInput = input as DateTimeInputDef
            const minValue = dtInput.min ? (typeof dtInput.min === 'function' ? dtInput.min() : dtInput.min) : undefined

            return (
                <Box key={dtInput.name} mb={2}>
                    <TextField
                        fullWidth
                        type="datetime-local"
                        label={dtInput.label}
                        placeholder={dtInput.placeholder}
                        value={inputsState[dtInput.name] ?? ''}
                        onChange={(e) => {
                            setInputsState({ ...inputsState, [dtInput.name]: e.target.value })
                        }}
                        slotProps={{
                            inputLabel: { shrink: true },
                            htmlInput: minValue ? { min: minValue } : undefined
                        }}
                    />
                </Box>
            )
        }

        // DROPDOWN -> use DropDownValue component (prefer cached options)
        const d = input as DropdownInputDef
        const opts = optionsCache[d.name] ?? []
        const valProp = d.valueProp ?? 'id'
        const dispProp = d.displayProp ?? 'name'
        const keyProp = d.keyProp ?? 'id'
        const multiple = !!d.multiple // default false


        return (
            <Box key={d.name} mb={2}>
                <DropDownValue
                    label={d.label}
                    items={opts}
                    selectedValue={inputsState[d.name] ?? (multiple ? [] : '')}
                    onSelection={(val: any) => {
                        // store value(s) as returned by DropDownValue:
                        // - single select -> scalar value
                        // - multi select -> array of values
                        setInputsState((prev: any) => ({ ...prev, [d.name]: val }))
                    }}
                    keyProp={keyProp}
                    valueProp={valProp}
                    displayProp={dispProp}
                    multiple={multiple}
                    disabled={loadingFetch[d.name]}
                    noOptionsText={loadingFetch[d.name] ? 'Loading...' : 'No options'}
                    style={{}}
                />
            </Box>
        )
    }

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Configure</Typography>
            {(feature.inputs ?? []).length === 0 ? (
                <Typography variant="body2">No configuration required for this feature.</Typography>
            ) : (
                (feature.inputs ?? []).map(renderInput)
            )}

            {/* Campaign-specific details */}
            {feature.DetailsComponent && inputsState['campaign_config_name'] && (
                <feature.DetailsComponent inputsState={inputsState} optionsCache={optionsCache} />
            )}
        </Paper>
    )
}